import { api } from "./api";
import axios from "axios";

export interface Transcript {
  transcript_id: string;
  text_raw: string;
  text_clean: string | null;
  meta: {
    upload_timestamp?: number;
    change_summary?: string;
    cleaner_model?: string;
    cleaning_duration_ms?: number;
    word_count_raw?: number;
    word_count_clean?: number;
  };
  created_at: string;
  updated_at: string;
}

export interface TranscriptResponse {
  success: boolean;
  has_transcript: boolean;
  transcript_status: "processing" | "cleaning" | "ready" | "error" | null;
  transcript?: Transcript;
}

export interface UploadResponse {
  success: boolean;
  message?: string;
  error?: string;
  change_summary?: string;
  word_count_raw?: number;
  word_count_clean?: number;
}

export const transcriptService = {
  /**
   * Upload a raw transcript for an interview
   */
  uploadTranscript: async (
    interviewId: string,
    rawText: string
  ): Promise<UploadResponse> => {
    try {
      const response = await api.post("/api/transcripts/upload", {
        interview_id: interviewId,
        raw_text: rawText,
      });
      return response.data;
    } catch (error: any) {
      console.error("Error uploading transcript:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to upload transcript",
      };
    }
  },

  /**
   * Get transcript for an interview
   */
  getTranscript: async (interviewId: string): Promise<TranscriptResponse> => {
    try {
      const response = await api.get(`/api/transcripts/${interviewId}`);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching transcript:", error);
      return {
        success: false,
        has_transcript: false,
        transcript_status: null,
      };
    }
  },

  /**
   * Cancel transcript processing and delete transcript data
   */
  cancelTranscript: async (
    interviewId: string
  ): Promise<{ success: boolean; error?: string; message?: string }> => {
    try {
      const response = await api.delete(`/api/transcripts/${interviewId}`);
      return response.data;
    } catch (error: any) {
      console.error("Error cancelling transcript:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to cancel transcript",
      };
    }
  },

  /**
   * Download all transcripts for a project as DOCX
   */
  downloadProjectTranscripts: async (projectId: string): Promise<void> => {
    try {
      const response = await axios.get(
        `${api.getBaseUrl()}/api/transcripts/download/${projectId}`,
        {
          responseType: "blob",
          withCredentials: true,
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Extract filename from response headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "project_transcripts.docx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error downloading transcripts:", error);
      throw new Error(
        error.response?.data?.error || "Failed to download transcripts"
      );
    }
  },
};
